import FetchClients, { ClientOutput } from "../client";
import {
  useMutation,
  useQuery as useReactQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { CamelCasedProperties } from "type-fest";
import { recursiveCamelCaseCipherTypesafe } from "@src/global_functions/postgrestApi";
import { throwBackpackResponseError } from "../errors";

const COMPLIANCE_REQUIREMENTS_QUERY_KEY = "compliance-requirements";
const COMPLIANCE_PROPERTY_TYPE_QUERY_KEY = "compliance-property-type";

const compliance = {
  useQueryComplianceRequirements,
  useQueryCompliancePropertyTypeOverride,
  mutations: {
    usePutComplianceRequirementsBlacklist,
    useDeleteComplianceRequirementsBlacklist,
    usePutCompliancePropertyTypeOverride,
  },
};

export type ComplianceRequirement = CamelCasedProperties<
  ClientOutput["site"]["ComplianceRequirement"]
>;
export type CompliancePropertyType =
  ClientOutput["site"]["CompliancePropertyType"];

export type CompliancePropertyTypeOverrideResponseSnakeCase =
  ClientOutput["site"]["SiteOverrideResponse"];
export type CompliancePropertyTypeOverrideResponse =
  CamelCasedProperties<CompliancePropertyTypeOverrideResponseSnakeCase>;

const getComplianceRequirementsForSite = async (siteId: number) => {
  const { data, error } = await FetchClients.site.GET(
    "/{site_id}/compliance-requirements",
    {
      params: {
        path: {
          site_id: siteId,
        },
      },
    }
  );
  if (error) {
    throw error;
  }
  return recursiveCamelCaseCipherTypesafe(data);
};

function useQueryComplianceRequirements(siteId: number) {
  return useReactQuery({
    queryKey: [COMPLIANCE_REQUIREMENTS_QUERY_KEY, siteId],
    queryFn: () => getComplianceRequirementsForSite(siteId),
  });
}

const getCompliancePropertyTypeOverrideForSite = async (siteId: number) => {
  const { data, error } = await FetchClients.site.GET(
    "/{site_id}/compliance-override",
    {
      params: {
        path: { site_id: siteId },
      },
    }
  );
  if (error) {
    throw error;
  }
  return recursiveCamelCaseCipherTypesafe(data);
};

function useQueryCompliancePropertyTypeOverride(siteId: number) {
  return useReactQuery({
    queryKey: [COMPLIANCE_PROPERTY_TYPE_QUERY_KEY, siteId],
    queryFn: () => getCompliancePropertyTypeOverrideForSite(siteId),
  });
}

const putCompliancePropertyTypeOverride = async (
  siteId: number,
  compliancePropertyType: CompliancePropertyType | null
) => {
  const { error } = await FetchClients.site.POST(
    "/{site_id}/compliance-override",
    {
      params: {
        path: {
          site_id: siteId,
        },
      },
      body: {
        current_override: compliancePropertyType,
      },
    }
  );

  if (error) {
    throwBackpackResponseError(error);
  }
};

function usePutCompliancePropertyTypeOverride(siteId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (propertyType: CompliancePropertyType | null) =>
      putCompliancePropertyTypeOverride(siteId, propertyType),
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [COMPLIANCE_PROPERTY_TYPE_QUERY_KEY, siteId],
      });
    },
  });
}

const putComplianceRequirementsBlacklist = async (
  siteId: number,
  requirementId: number
) => {
  const { response } = await FetchClients.site.PUT(
    "/{site_id}/compliance-requirements-blacklist/{requirement_id}",
    {
      params: {
        path: {
          site_id: siteId,
          requirement_id: requirementId,
        },
      },
    }
  );

  if (response.status !== 201) {
    throw new Error("Unable to add compliance requirement to blacklist");
  }
};

function usePutComplianceRequirementsBlacklist(siteId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requirementId: number) =>
      putComplianceRequirementsBlacklist(siteId, requirementId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [COMPLIANCE_REQUIREMENTS_QUERY_KEY, siteId],
      });
    },
  });
}

const deleteComplianceRequirementsBlacklist = async (
  siteId: number,
  requirementId: number
) => {
  const { response } = await FetchClients.site.DELETE(
    "/{site_id}/compliance-requirements-blacklist/{requirement_id}",
    {
      params: {
        path: {
          site_id: siteId,
          requirement_id: requirementId,
        },
      },
    }
  );

  if (response.status !== 204) {
    throw new Error("Unable to remove compliance requirement from blacklist");
  }
};

function useDeleteComplianceRequirementsBlacklist(siteId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requirementId: number) =>
      deleteComplianceRequirementsBlacklist(siteId, requirementId),
    onSuccess: async () =>
      await queryClient.invalidateQueries({
        queryKey: [COMPLIANCE_REQUIREMENTS_QUERY_KEY, siteId],
      }),
  });
}

export default compliance;
